import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Link2,
  Camera,
  Image as ImageIcon,
  ScanBarcode,
  X,
  Loader2,
  Check,
  Search,
  Palette,
  ClipboardPaste,
  ArrowLeft,
} from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Clipboard } from "@capacitor/clipboard";

import { DraggableBottomSheet } from "@/components/ui/DraggableBottomSheet";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useCollections } from "@/hooks/useCollections";
import { useRegionalSettings } from "@/hooks/useRegionalSettings";
import { useProfile } from "@/hooks/useProfile";
import { AuthDialog } from "@/components/auth/AuthDialog";
import { VisualSearchSheet } from "@/components/saves/VisualSearchSheet";
import { BarcodeScannerDialog } from "@/components/saves/BarcodeScannerDialog";
import { ProductDiscoveryResults } from "@/components/saves/ProductDiscoveryResults";
import { supabase } from "@/integrations/supabase/client";
import {
  extractImageLocation,
  reverseGeocode,
} from "@/utils/extractImageLocation";
import { extractBrandHintsFromCaption } from "@/utils/extractBrandHintsFromCaption";

// Platform logos
import tiktokLogo from "@/assets/tiktok-logo.png";
import pinterestLogo from "@/assets/pinterest-logo.png";
import instagramLogo from "@/assets/instagram-logo.png";
import youtubeLogo from "@/assets/youtube-logo.png";
import nikeLogo from "@/assets/nike-logo.svg";
import asosLogo from "@/assets/asos-logo.svg";
import zaraLogo from "@/assets/zara-logo.svg";
import hmLogo from "@/assets/hm-logo.svg";
import sheinLogo from "@/assets/shein-logo.svg";
import uniqloLogo from "@/assets/uniqlo-logo.svg";
import adidasLogo from "@/assets/adidas-logo.svg";
import amazonLogo from "@/assets/amazon-logo.svg";

// Source platforms with logos - randomized order for visual variety
const SOURCE_PLATFORMS = [
  { name: "TikTok", logo: tiktokLogo },
  { name: "Zara", logo: zaraLogo },
  { name: "Pinterest", logo: pinterestLogo },
  { name: "Nike", logo: nikeLogo },
  { name: "Instagram", logo: instagramLogo },
  { name: "SHEIN", logo: sheinLogo },
  { name: "YouTube", logo: youtubeLogo },
  { name: "ASOS", logo: asosLogo },
  { name: "Amazon", logo: amazonLogo },
  { name: "Uniqlo", logo: uniqloLogo },
  { name: "H&M", logo: hmLogo },
  { name: "Adidas", logo: adidasLogo },
];

interface AddItemSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  /** When set, newly saved items will be automatically added to this collection */
  autoAddToCollectionId?: string;
  /** Collection name for toast message */
  autoAddToCollectionName?: string;
}

interface ExtractedVariant {
  color?: string;
  size?: string;
  sku?: string;
  price?: string;
  available?: boolean;
  imageUrl?: string;
}

interface ExtractedData {
  title: string;
  description: string;
  price?: string | null;
  brand?: string | null;
  category?: string;
  image?: string | null;
  images?: string[];
  platform?: string;
  url?: string;
  extracted?: boolean;
  rating?: number | null;
  reviewCount?: number | null;
  person?: {
    identified: boolean;
    name?: string;
    role?: string;
  } | null;
  products?: Array<{
    name: string;
    category?: string;
    estimatedPrice?: string;
    brand?: string;
    color?: string;
    material?: string;
  }>;
  tags?: string[];
  colors?: Array<{ name: string; hex?: string }>;
  focalPoint?: { x: number; y: number } | null;
  creatorName?: string | null;
  creatorBioLink?: string | null;
  variants?: ExtractedVariant[];
  fluxPageId?: string | null;
  brandHints?: string[];
  fullCaption?: string;
}

interface ImageAnalysisData {
  title: string;
  description: string;
  person?: {
    identified: boolean;
    name?: string;
    role?: "influencer" | "celebrity" | "model" | "unknown";
  } | null;
  products: Array<{
    name: string;
    category: string;
    estimatedPrice?: string;
    brand?: string;
    color?: string;
    material?: string;
  }>;
  colors: Array<{
    name: string;
    hex?: string;
  }>;
  style: {
    primary: string;
    tags: string[];
  };
  category: string;
  searchTerms?: string[];
  focalPoint?: { x: number; y: number } | null;
}

export function AddItemSheet({
  open,
  onOpenChange,
  initialUrl,
  autoAddToCollectionId,
  autoAddToCollectionName,
}: AddItemSheetProps) {
  const [url, setUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(
    null,
  );
  const [imageAnalysis, setImageAnalysis] = useState<ImageAnalysisData | null>(
    null,
  );
  const [isExtracting, setIsExtracting] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [showVisualSearch, setShowVisualSearch] = useState(false);

  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [urlAutoPasted, setUrlAutoPasted] = useState(false);
  const [isTypingUrl, setIsTypingUrl] = useState(false);
  const [pendingAutoSave, setPendingAutoSave] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);

  // Product discovery state
  const [isDiscoveringProducts, setIsDiscoveringProducts] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState<{
    imageTitle: string;
    categories: Array<{
      category: string;
      displayName: string;
      products: Array<{
        id: string;
        name: string;
        category: string;
        price: number | null;
        currency: string;
        formattedPrice: string;
        image: string;
        url: string;
        store: string;
        originalProduct: string;
      }>;
    }>;
    totalProducts: number;
  } | null>(null);
  const typeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pastedUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, createItem, updateItem, uploadImage } = useSavedItems();
  const { addItemToCollection } = useCollections();
  const { settings, convertPrice, canConvert } = useRegionalSettings();
  const { profile } = useProfile();

  const canSaveItem = true; // No limits during early access

  // Determine input mode based on what's been provided
  const inputMode: "url" | "image" | null = selectedImage
    ? "image"
    : url
      ? "url"
      : null;

  // Convert a price range string like "$80-$120" to user's currency
  const formatEstimatedPrice = useCallback(
    (priceStr: string | undefined): string | null => {
      if (!priceStr) return null;

      let sourceCurrency = "USD";
      if (priceStr.includes("£")) sourceCurrency = "GBP";
      else if (priceStr.includes("€")) sourceCurrency = "EUR";
      else if (priceStr.includes("¥")) sourceCurrency = "JPY";
      else if (priceStr.includes("₹")) sourceCurrency = "INR";
      else if (priceStr.includes("CA$") || priceStr.includes("C$"))
        sourceCurrency = "CAD";
      else if (priceStr.includes("A$")) sourceCurrency = "AUD";

      if (sourceCurrency === settings.currency) return priceStr;
      if (!canConvert(sourceCurrency, settings.currency)) return priceStr;

      const numbers = priceStr.match(/[\d,]+(?:\.\d+)?/g);
      if (!numbers || numbers.length === 0) return priceStr;

      const parsed = numbers.map((n) => parseFloat(n.replace(/,/g, "")));
      const converted = parsed
        .filter((n) => n > 0)
        .map((n) => {
          const c = convertPrice(n, sourceCurrency, settings.currency);
          return Math.round(c);
        });

      if (converted.length === 0) return priceStr;

      const symbol = settings.currencySymbol;
      if (converted.length === 1) {
        return `${symbol}${converted[0].toLocaleString()}`;
      }
      const min = Math.min(...converted);
      const max = Math.max(...converted);
      if (min === max) return `${symbol}${min.toLocaleString()}`;
      return `${symbol}${min.toLocaleString()}-${symbol}${max.toLocaleString()}`;
    },
    [settings.currency, settings.currencySymbol, convertPrice, canConvert],
  );

  // Set initial URL from bookmarklet
  useEffect(() => {
    if (initialUrl && open) {
      setUrl(initialUrl);
    }
  }, [initialUrl, open]);

  // Handle explicit paste button click (works on mobile as it's a user gesture)
  const handlePasteClick = async () => {
    try {
      let text: string | undefined;

      if (Capacitor.isNativePlatform()) {
        const result = await Clipboard.read();
        text = result.value;
      } else {
        if (!navigator.clipboard?.readText) {
          toast("Use long-press to paste manually");
          return;
        }
        text = await navigator.clipboard.readText();
      }

      if (text) {
        const trimmedText = text.trim();
        if (
          trimmedText.startsWith("http://") ||
          trimmedText.startsWith("https://") ||
          trimmedText.startsWith("www.")
        ) {
          const urlToSet = trimmedText.startsWith("www.")
            ? `https://${trimmedText}`
            : trimmedText;

          // Store full URL in ref immediately for auto-save
          pastedUrlRef.current = urlToSet;

          // Typewriter effect - type out the URL character by character
          setIsTypingUrl(true);
          const typingDuration = 800;
          const charDelay = Math.max(15, typingDuration / urlToSet.length);

          let currentIndex = 0;
          typeIntervalRef.current = setInterval(() => {
            currentIndex++;
            setUrl(urlToSet.slice(0, currentIndex));

            if (currentIndex >= urlToSet.length) {
              if (typeIntervalRef.current) {
                clearInterval(typeIntervalRef.current);
                typeIntervalRef.current = null;
              }
              setIsTypingUrl(false);
              setUrlAutoPasted(true);
              setPendingAutoSave(true);
            }
          }, charDelay);
        } else {
          toast("Copy a product link first, then tap Paste");
        }
      }
    } catch {
      // Clipboard access denied
      toast("Allow clipboard access or paste manually");
    }
  };

  // Handle native paste event (Cmd+V or long-press paste)
  const handleNativePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData?.getData("text");
    if (text) {
      const trimmed = text.trim();
      if (
        trimmed.startsWith("http://") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("www.")
      ) {
        e.preventDefault();
        const urlToSet = trimmed.startsWith("www.")
          ? `https://${trimmed}`
          : trimmed;
        pastedUrlRef.current = urlToSet;
        setUrl(urlToSet);
        setUrlAutoPasted(true);
        setPendingAutoSave(true);
      }
    }
  };

  // Auto-grab URL from clipboard when sheet opens
  // Skip on native platforms to avoid iOS clipboard permission prompts
  const autoGrabClipboardUrl = useCallback(async () => {
    // On native iOS/Android, Clipboard.read() triggers a system permission prompt
    // every time. Skip auto-grab and let users tap the Paste button instead.
    if (Capacitor.isNativePlatform()) return;

    try {
      let text: string | undefined;

      if (!navigator.clipboard?.readText) return;
      text = await navigator.clipboard.readText();

      if (!text) return;

      const trimmedText = text.trim();
      // Only auto-paste if it's clearly a URL
      if (
        trimmedText.startsWith("http://") ||
        trimmedText.startsWith("https://") ||
        (trimmedText.startsWith("www.") && trimmedText.includes("."))
      ) {
        const urlToSet = trimmedText.startsWith("www.")
          ? `https://${trimmedText}`
          : trimmedText;

        // Store full URL in ref immediately for auto-save
        pastedUrlRef.current = urlToSet;

        // Use typewriter effect for visual feedback
        setIsTypingUrl(true);
        const typingDuration = 600;
        const charDelay = Math.max(10, typingDuration / urlToSet.length);

        let currentIndex = 0;
        typeIntervalRef.current = setInterval(() => {
          currentIndex++;
          setUrl(urlToSet.slice(0, currentIndex));

          if (currentIndex >= urlToSet.length) {
            if (typeIntervalRef.current) {
              clearInterval(typeIntervalRef.current);
              typeIntervalRef.current = null;
            }
            setIsTypingUrl(false);
            setUrlAutoPasted(true);
            setPendingAutoSave(true);
          }
        }, charDelay);
      }
    } catch {
      // Silently fail - clipboard access denied or not available
    }
  }, []);

  // Reset state when sheet opens
  useEffect(() => {
    if (typeIntervalRef.current) {
      clearInterval(typeIntervalRef.current);
      typeIntervalRef.current = null;
    }

    if (open) {
      setUrl("");
      setSelectedImage(null);
      setSelectedImageFile(null);
      setIsSavingImage(false);
      setExtractedData(null);
      setImageAnalysis(null);
      setUrlAutoPasted(false);
      setPendingAutoSave(false);
      setIsTypingUrl(false);
      pastedUrlRef.current = null;
      setDiscoveryResults(null);
      setIsDiscoveringProducts(false);

      // Auto-grab URL from clipboard after a short delay to ensure sheet is mounted
      // Only if no initialUrl was provided
      if (!initialUrl) {
        setTimeout(() => {
          autoGrabClipboardUrl();
        }, 150);
      }
    }

    return () => {
      if (typeIntervalRef.current) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
    };
  }, [open, initialUrl, autoGrabClipboardUrl]);

  // Reset auto-paste state when URL changes manually
  const handleUrlChange = (newUrl: string) => {
    setUrl(newUrl);
    setUrlAutoPasted(false);
    setExtractedData(null);
    pastedUrlRef.current = null;
  };

  // Immediately save image and close sheet when selected - background processing
  const handleImmediateImageSave = useCallback(
    async (imageFile: File, imageBase64: string) => {
      if (!user) {
        setShowAuthDialog(true);
        return;
      }

      try {
        // Show saving state immediately
        setSelectedImage(imageBase64);
        setIsSavingImage(true);

        // Extract GPS location from EXIF (fast, runs in parallel with upload)
        const [imageUrl, photoLocation] = await Promise.all([
          uploadImage(imageFile),
          extractImageLocation(imageFile),
        ]);

        // Create skeleton item immediately with analyzing state and location
        const skeletonItem = await createItem.mutateAsync({
          image_url: imageUrl,
          source_type: "image",
          name: "Analyzing image...",
          ai_analyzed: false,
          photo_location: photoLocation,
        });

        // Auto-add to collection if specified
        if (autoAddToCollectionId && skeletonItem?.id) {
          try {
            await addItemToCollection.mutateAsync({
              collectionId: autoAddToCollectionId,
              savedItemId: skeletonItem.id,
            });
            // Processing in background silently
          } catch (e) {
            console.error("Failed to add to collection:", e);
            // Processing in background silently
          }
        } else {
          // Processing in background silently
        }

        // Close sheet and confirm
        onOpenChange(false);
        toast.success('Image saved');

        // Navigate to saves if not already there
        if (location.pathname !== "/app" && !autoAddToCollectionId) {
          navigate("/app");
        }

        // Run analysis and reverse geocoding in background (fire and forget)
        (async () => {
          try {
            // Start reverse geocoding if we have coordinates but no place name
            let placeNamePromise: Promise<string | null> | null = null;
            if (photoLocation && !photoLocation.placeName) {
              placeNamePromise = reverseGeocode(
                photoLocation.latitude,
                photoLocation.longitude,
              );
            }

            const { data, error } = await supabase.functions.invoke(
              "analyze-image",
              {
                body: { imageBase64 },
              },
            );

            // Get the place name result
            const placeName = placeNamePromise ? await placeNamePromise : null;

            if (!error && data?.success && data?.data) {
              const analysis = data.data;
              await updateItem.mutateAsync({
                itemId: skeletonItem.id,
                updates: {
                  name: analysis.title,
                  note: analysis.description,
                  ai_analyzed: true,
                  ai_summary: analysis.description,
                  ai_category: analysis.category,
                  ai_products: analysis.products || [],
                  ai_tags: analysis.style?.tags || [],
                  ai_person: analysis.person || null,
                  ai_focal_point: analysis.focalPoint || null,
                  // Update location with place name if we got one
                  ...(placeName && photoLocation
                    ? {
                        photo_location: { ...photoLocation, placeName },
                      }
                    : {}),
                },
              });
            } else {
              // Mark as analyzed even if failed to prevent perpetual loading
              await updateItem.mutateAsync({
                itemId: skeletonItem.id,
                updates: {
                  ai_analyzed: true,
                  name: "Saved Image",
                  // Still update place name if available
                  ...(placeName && photoLocation
                    ? {
                        photo_location: { ...photoLocation, placeName },
                      }
                    : {}),
                },
              });
            }
          } catch (e) {
            console.error("Background image analysis error:", e);
            // Mark as analyzed to clear loading state
            try {
              await updateItem.mutateAsync({
                itemId: skeletonItem.id,
                updates: { ai_analyzed: true, name: "Saved Image" },
              });
            } catch {}
          }
        })();
      } catch (error) {
        console.error("Image save error:", error);
        toast.error("Failed to save image");
      }
    },
    [
      user,
      canSaveItem,
      uploadImage,
      createItem,
      updateItem,
      addItemToCollection,
      autoAddToCollectionId,
      autoAddToCollectionName,
      onOpenChange,
      navigate,
      location.pathname,
    ],
  );

  const extractUrl = async (urlToExtract: string) => {
    setIsExtracting(true);
    setExtractedData(null);

    try {
      const { data, error } = await supabase.functions.invoke("extract-url", {
        body: { url: urlToExtract },
      });

      if (error) {
        console.error("Extract error:", error);
        toast.error("Could not analyze this URL. You can still save it.");
        return;
      }

      if (data?.success && data?.data) {
        setExtractedData(data.data);
      }
    } catch (error) {
      console.error("Extract error:", error);
    } finally {
      setIsExtracting(false);
    }
  };

  const analyzeImage = async (imageBase64: string) => {
    setIsAnalyzingImage(true);
    setImageAnalysis(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-image", {
        body: { imageBase64 },
      });

      if (error) {
        console.error("Image analysis error:", error);
        toast.error("Could not analyze this image. You can still save it.");
        return;
      }

      if (data?.success && data?.data) {
        setImageAnalysis(data.data);
      }
    } catch (error) {
      console.error("Image analysis error:", error);
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  // Discover shoppable products from image
  const discoverProductsFromImage = async (imageBase64: string) => {
    setIsDiscoveringProducts(true);
    setDiscoveryResults(null);
    // Also run the regular analysis for saving the image itself
    analyzeImage(imageBase64);

    try {
      const { data, error } = await supabase.functions.invoke(
        "extract-products-from-image",
        {
          body: {
            imageBase64,
            // IMPORTANT: backend expects ISO country code (e.g., GB), not display name (e.g., United Kingdom)
            country: settings.countryCode || "US",
            currency: settings.currency || "USD",
            gender: profile?.gender || null,
          },
        },
      );

      if (error) {
        console.error("Product discovery error:", error);
        toast.error("Could not find products. Try again or save the image.");
        return;
      }

      if (data?.success && data?.data) {
        setDiscoveryResults(data.data);
        if (data.data.totalProducts > 0) {
          // Discovery results shown in UI
        }
      }
    } catch (error) {
      console.error("Product discovery error:", error);
    } finally {
      setIsDiscoveringProducts(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Read file and immediately save
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        handleImmediateImageSave(file, base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = () => {
    // Use the pre-existing camera input ref for better iOS compatibility
    cameraInputRef.current?.click();
  };

  const handleCameraInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Read file and immediately save
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        handleImmediateImageSave(file, base64);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    e.target.value = "";
  };

  const handleGalleryPick = () => {
    fileInputRef.current?.click();
  };

  const handleBarcodeScanner = () => {
    setShowBarcodeScanner(true);
  };

  const clearImage = () => {
    setSelectedImage(null);
    setSelectedImageFile(null);
    setImageAnalysis(null);
  };

  const extractDomainFromUrl = (url: string): string => {
    try {
      const domain = new URL(url).hostname.replace("www.", "");
      return domain;
    } catch {
      return "";
    }
  };

  const getPlatformColor = (platform?: string) => {
    switch (platform?.toLowerCase()) {
      case "instagram":
        return "bg-gradient-to-r from-purple-500 to-pink-500";
      case "tiktok":
        return "bg-black";
      case "pinterest":
        return "bg-red-500";
      case "youtube":
        return "bg-red-600";
      default:
        return "bg-primary";
    }
  };

  const handleSave = async () => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    // Use the full pasted URL from ref if available (avoids typewriter truncation)
    const currentUrl = pastedUrlRef.current || url;
    const currentInputMode = inputMode;
    const currentSelectedImage = selectedImage;
    const currentSelectedImageFile = selectedImageFile;
    const currentExtractedData = extractedData;
    const currentImageAnalysis = imageAnalysis;

    // Helper to get source type
    const getSourceType = (): "url" | "image" | "barcode" => {
      if (currentInputMode === "image") return "image";
      return "url";
    };

    // Helper to extract domain
    const getDomain = (urlStr: string): string => {
      try {
        return new URL(urlStr).hostname.replace("www.", "");
      } catch {
        return "";
      }
    };

    try {
      // For images, we need to upload first before closing (quick operation)
      let initialImageUrl: string | undefined;
      if (currentInputMode === "image" && currentSelectedImageFile) {
        initialImageUrl = await uploadImage(currentSelectedImageFile);
      } else if (
        currentInputMode === "image" &&
        currentSelectedImage?.startsWith("http")
      ) {
        initialImageUrl = currentSelectedImage;
      }

      // If we already have extracted data or image analysis, use it immediately
      if (currentExtractedData?.extracted || currentImageAnalysis) {
        // We have full data - create complete item and close
        const getTitle = () => {
          if (currentInputMode === "image" && currentImageAnalysis)
            return currentImageAnalysis.title;
          if (currentInputMode === "url" && currentExtractedData)
            return currentExtractedData.title;
          return undefined;
        };

        const getDescription = () => {
          if (currentInputMode === "image" && currentImageAnalysis)
            return currentImageAnalysis.description;
          if (currentInputMode === "url" && currentExtractedData) {
            // For social saves, store the full caption as note so brand-rich text
            // is available later for product discovery (extract-products-from-image)
            if (currentExtractedData.fullCaption) {
              return currentExtractedData.fullCaption;
            }
            return currentExtractedData.description;
          }
          return undefined;
        };

        const getPrice = () => {
          if (currentInputMode === "url" && currentExtractedData?.price) {
            const numbers =
              currentExtractedData.price.match(/[\d,]+(?:\.\d+)?/g);
            if (numbers) {
              const parsed = numbers.map((n) =>
                parseFloat(n.replace(/,/g, "")),
              );
              return Math.min(...parsed.filter((n) => n > 0));
            }
          }
          if (
            currentInputMode === "image" &&
            currentImageAnalysis?.products?.[0]?.estimatedPrice
          ) {
            const priceStr = currentImageAnalysis.products[0].estimatedPrice;
            const numbers = priceStr.match(/[\d,]+(?:\.\d+)?/g);
            if (numbers) {
              const parsed = numbers.map((n) =>
                parseFloat(n.replace(/,/g, "")),
              );
              return Math.min(...parsed.filter((n) => n > 0));
            }
          }
          return undefined;
        };

        const getAIData = () => {
          if (currentInputMode === "image" && currentImageAnalysis) {
            return {
              ai_analyzed: true,
              ai_summary: currentImageAnalysis.description,
              ai_category: currentImageAnalysis.category,
              ai_products: currentImageAnalysis.products,
              ai_tags: currentImageAnalysis.style?.tags || [],
              ai_person: currentImageAnalysis.person || null,
              ai_focal_point: currentImageAnalysis.focalPoint || null,
            };
          }
          if (currentInputMode === "url" && currentExtractedData) {
            return {
              ai_analyzed: true,
              ai_summary: currentExtractedData.description,
              ai_category: currentExtractedData.category,
              ai_products: currentExtractedData.products || [],
              ai_tags: currentExtractedData.tags || [],
              ai_person: currentExtractedData.person || null,
              ai_focal_point: currentExtractedData.focalPoint || null,
              ai_variants: currentExtractedData.variants || [],
              creator_name: currentExtractedData.creatorName || null,
              creator_bio_link: currentExtractedData.creatorBioLink || null,
              image_urls: currentExtractedData.images || [],
              ai_brand_hints: currentExtractedData.brandHints || [],
            };
          }
          return {};
        };

        let imageUrl = initialImageUrl;
        if (
          currentInputMode === "url" &&
          currentExtractedData?.image?.startsWith("http")
        ) {
          imageUrl = currentExtractedData.image;
        }

        const fluxPageId =
          currentInputMode === "url"
            ? currentExtractedData?.fluxPageId
            : undefined;

        const createdItem = await createItem.mutateAsync({
          // When flux_pages reference is available, skip product data fields
          ...(fluxPageId
            ? {}
            : {
                url: currentInputMode === "url" ? currentUrl : undefined,
                image_url: imageUrl,
                store:
                  currentInputMode === "url" && currentUrl
                    ? currentExtractedData?.platform || getDomain(currentUrl)
                    : undefined,
                name: getTitle(),
                price: getPrice(),
              }),
          ...(!fluxPageId
            ? {}
            : {
                price_at_save: getPrice(),
              }),
          note: getDescription(),
          source_type: getSourceType(),
          page_id: fluxPageId || undefined,
          ...getAIData(),
        });

        // Auto-add to collection if specified
        if (autoAddToCollectionId && createdItem?.id) {
          try {
            await addItemToCollection.mutateAsync({
              collectionId: autoAddToCollectionId,
              savedItemId: createdItem.id,
            });
            // Added silently
          } catch (e) {
            console.error("Failed to add to collection:", e);
            // Added silently
          }
        } else {
          // Added silently
        }

        onOpenChange(false);
        return;
      }

      // OPTIMISTIC FLOW: Create skeleton item immediately, close sheet, extract in background
      const skeletonItem = await createItem.mutateAsync({
        url: currentInputMode === "url" ? currentUrl : undefined,
        image_url: initialImageUrl,
        source_type: getSourceType(),
        store:
          currentInputMode === "url" && currentUrl
            ? getDomain(currentUrl)
            : undefined,
        name:
          currentInputMode === "url" ? getDomain(currentUrl) : "Analyzing...",
        ai_analyzed: false,
      });

      // Auto-add to collection if specified
      if (autoAddToCollectionId && skeletonItem?.id) {
        try {
          await addItemToCollection.mutateAsync({
            collectionId: autoAddToCollectionId,
            savedItemId: skeletonItem.id,
          });
          // Processing in background silently
        } catch (e) {
          console.error("Failed to add to collection:", e);
          // Processing in background silently
        }
      } else {
        // Processing in background silently
      }

      // Close sheet immediately
      onOpenChange(false);

      // Navigate to saves if not already there (skip if viewing a collection)
      if (location.pathname !== "/app" && !autoAddToCollectionId) {
        navigate("/app");
      }

      // Run extraction in background (fire and forget with error handling)
      (async () => {
        try {
          if (currentInputMode === "url" && currentUrl) {
            const isValidUrl =
              currentUrl.startsWith("http://") ||
              currentUrl.startsWith("https://");
            if (isValidUrl) {
              const { data, error } = await supabase.functions.invoke(
                "extract-url",
                {
                  body: { url: currentUrl },
                },
              );

              if (!error && data?.success && data?.data) {
                const extracted = data.data;

                // Parse price
                let price: number | undefined;
                if (extracted.price) {
                  const numbers = extracted.price.match(/[\d,]+(?:\.\d+)?/g);
                  if (numbers) {
                    const parsed = numbers.map((n: string) =>
                      parseFloat(n.replace(/,/g, "")),
                    );
                    price = Math.min(...parsed.filter((n: number) => n > 0));
                  }
                }

                // Parse original price (for sale items)
                let originalPrice: number | undefined;
                if (extracted.originalPrice) {
                  const numbers =
                    extracted.originalPrice.match(/[\d,]+(?:\.\d+)?/g);
                  if (numbers) {
                    const parsed = numbers.map((n: string) =>
                      parseFloat(n.replace(/,/g, "")),
                    );
                    originalPrice = Math.min(
                      ...parsed.filter((n: number) => n > 0),
                    );
                  }
                }

                // Update the item with extracted data
                const updates: Record<string, unknown> = {
                  platform: extracted.platform,
                  ai_analyzed: true,
                  ai_summary: extracted.description,
                  ai_category: extracted.category,
                  ai_products: extracted.products || [],
                  ai_tags: extracted.tags || [],
                  ai_person: extracted.person || null,
                  ai_focal_point: extracted.focalPoint || null,
                  creator_name: extracted.creatorName || null,
                  creator_bio_link: extracted.creatorBioLink || null,
                  rating: extracted.rating || null,
                  review_count: extracted.reviewCount || null,
                };

                if (extracted.fluxPageId) {
                  // Product data lives in flux_pages
                  (updates as any).page_id = extracted.fluxPageId;
                  (updates as any).price_at_save = price ?? null;
                } else {
                  // No flux_pages entry - write product data directly
                  updates.name = extracted.title;
                  updates.note = extracted.description;
                  updates.image_url = extracted.image || undefined;
                  updates.image_urls = extracted.images || [];
                  updates.price = price;
                  updates.original_price = originalPrice;
                  updates.store = extracted.platform || getDomain(currentUrl);
                }

                await updateItem.mutateAsync({
                  itemId: skeletonItem.id,
                  updates: updates as any,
                });
              }
            }
          }
        } catch (e) {
          console.error("Background extraction error:", e);
          // Don't show error toast - item is saved, just not enriched
        }
      })();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save item",
      );
    }
  };

  // Auto-save when a URL is pasted (via clipboard grab, paste button, or native paste)
  useEffect(() => {
    if (pendingAutoSave && url && !isTypingUrl) {
      // Use the full pasted URL from ref to avoid race conditions with typewriter effect
      if (pastedUrlRef.current) {
        setUrl(pastedUrlRef.current);
      }
      setPendingAutoSave(false);
      // Small delay to ensure state is settled before saving
      const t = setTimeout(() => handleSave(), 50);
      return () => clearTimeout(t);
    }
  }, [pendingAutoSave, url, isTypingUrl]);

  const isCurrentlyAnalyzing = isExtracting || isAnalyzingImage;
  const hasContent = url.trim().length > 0 || selectedImage !== null;
  const hasAnalysis = extractedData?.extracted || imageAnalysis;

  const canSave = () => {
    if (inputMode === "url") return url.trim().length > 0 && !isExtracting;
    if (inputMode === "image")
      return selectedImage !== null && !isAnalyzingImage;
    return false;
  };

  // Compact preview for URL extraction
  const renderUrlPreview = () => {
    if (!extractedData) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-border bg-background overflow-hidden shadow-sm"
      >
        <div className="flex gap-3 p-3">
          {extractedData.image && (
            <img
              src={extractedData.image}
              alt={extractedData.title}
              className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground line-clamp-2 text-sm">
              {extractedData.title}
            </h4>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {extractedData.price && (
                <span className="text-primary font-semibold text-sm">
                  {extractedData.price}
                </span>
              )}
              {extractedData.platform && (
                <span
                  className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-medium text-white",
                    getPlatformColor(extractedData.platform),
                  )}
                >
                  {extractedData.platform}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render product discovery view for images
  const renderImagePreview = () => {
    if (!selectedImage) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-border bg-background overflow-hidden shadow-sm"
      >
        {/* Source image thumbnail with back button */}
        <div className="flex items-center gap-3 p-3 border-b border-border">
          <img
            src={selectedImage}
            alt="Source"
            className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {discoveryResults?.imageTitle ||
                imageAnalysis?.title ||
                "Analyzing image..."}
            </p>
            <p className="text-xs text-muted-foreground">
              {isDiscoveringProducts
                ? "Finding shoppable products..."
                : discoveryResults
                  ? `${discoveryResults.totalProducts} items found`
                  : "Processing..."}
            </p>
          </div>
          <button
            onClick={clearImage}
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Product discovery results */}
        <div className="p-3 max-h-[50vh] overflow-y-auto">
          <ProductDiscoveryResults
            imageTitle={discoveryResults?.imageTitle || "Style Inspiration"}
            categories={discoveryResults?.categories || []}
            totalProducts={discoveryResults?.totalProducts || 0}
            isLoading={isDiscoveringProducts}
            onClose={clearImage}
          />
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <DraggableBottomSheet
            open={open}
            onOpenChange={onOpenChange}
            height="auto"
          >
            <div className="pb-6 px-4">
              {/* Header - Exciting tagline with auto-scrolling source carousel */}
              <div className="text-center mb-6 pt-1">
                <h2 className="text-xl font-bold text-foreground leading-tight mb-1">
                  Add{" "}
                  <span className="underline decoration-primary decoration-2 underline-offset-4">
                    anything
                  </span>
                  . We'll do the rest.
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Links, photos, social posts, or barcodes
                </p>
                {/* Auto-scrolling source carousel */}
                <div className="overflow-hidden -mx-4">
                  <div
                    className="flex items-center gap-3 w-max animate-marquee"
                    style={{
                      animation: "marquee 20s linear infinite",
                    }}
                  >
                    {/* Double the items for seamless loop */}
                    {[
                      ...SOURCE_PLATFORMS,
                      { name: "+1m Stores", logo: null },
                      { name: "IRL", logo: null },
                      { name: "Screenshots", logo: null },
                      ...SOURCE_PLATFORMS,
                      { name: "+1m Stores", logo: null },
                      { name: "IRL", logo: null },
                      { name: "Screenshots", logo: null },
                    ].map((source, idx) => (
                      <span
                        key={`${source.name}-${idx}`}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap",
                          source.logo
                            ? "bg-surface-2 text-muted-foreground"
                            : "bg-primary/10 text-primary",
                        )}
                      >
                        {source.logo && (
                          <img
                            src={source.logo}
                            alt={source.name}
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        {source.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saving image state */}
              {isSavingImage && selectedImage && (
                <div className="flex flex-col items-center gap-4 py-6 mb-5">
                  <div className="relative w-32 h-32 rounded-2xl overflow-hidden">
                    <img
                      src={selectedImage}
                      alt="Saving"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-background/40 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-foreground" />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Saving...</span>
                </div>
              )}

              {/* Quick Capture Buttons - Only show when no image selected */}
              {!selectedImage && !isSavingImage && (
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <button
                    onClick={handleCameraCapture}
                    className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-surface-2 hover:bg-surface-3 border-2 border-transparent transition-all active:scale-95"
                  >
                    <div className="w-11 h-11 rounded-xl bg-surface-3 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      Camera
                    </span>
                  </button>

                  <button
                    onClick={handleGalleryPick}
                    className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-surface-2 hover:bg-surface-3 border-2 border-transparent transition-all active:scale-95"
                  >
                    <div className="w-11 h-11 rounded-xl bg-surface-3 flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      Gallery
                    </span>
                  </button>

                  <button
                    onClick={handleBarcodeScanner}
                    className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-surface-2 hover:bg-surface-3 border-2 border-transparent transition-all active:scale-95"
                  >
                    <div className="w-11 h-11 rounded-xl bg-surface-3 flex items-center justify-center">
                      <ScanBarcode className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      Barcode
                    </span>
                  </button>
                </div>
              )}

              {/* Divider - only show when no image selected */}
              {!selectedImage && !isSavingImage && (
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}

              {/* URL Input - Only show when no image */}
              {!selectedImage && !isSavingImage && (
                <div className="mb-5">
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                      <Link2 className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      inputMode="url"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={url}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      onPaste={handleNativePaste}
                      placeholder="Paste a link"
                      className={cn(
                        "w-full bg-surface-2 border-2 rounded-2xl pl-11 py-4 text-foreground placeholder:text-muted-foreground focus:outline-none transition-all",
                        url
                          ? "border-primary/50 focus:border-primary pr-12"
                          : "border-primary/40 animate-glow-input pr-24",
                      )}
                    />
                    {/* Paste button - only show when input is empty */}
                    {!url && !isTypingUrl && (
                      <button
                        onClick={handlePasteClick}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium active:scale-95 transition-transform"
                      >
                        <ClipboardPaste className="w-3.5 h-3.5" />
                        Paste
                      </button>
                    )}
                    {/* Clear URL button */}
                    {url && !isExtracting && (
                      <button
                        onClick={() => handleUrlChange("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors"
                        aria-label="Clear link"
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                    )}
                    {isExtracting && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                    )}
                  </div>

                  {/* Auto-paste indicator */}
                </div>
              )}

              {/* Previews */}
              <AnimatePresence mode="wait">
                {inputMode === "url" && extractedData && (
                  <div className="mb-5">{renderUrlPreview()}</div>
                )}
                {inputMode === "image" && (
                  <div className="mb-5">{renderImagePreview()}</div>
                )}
              </AnimatePresence>

              {/* AI Status Indicator - only show when actively analyzing or has results */}
              {(isCurrentlyAnalyzing || hasAnalysis) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "flex items-center gap-3 p-3.5 rounded-2xl mb-5 transition-colors",
                    hasAnalysis ? "bg-green-500/10" : "bg-surface-2",
                  )}
                >
                  <div
                    className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0",
                      isCurrentlyAnalyzing && "animate-pulse",
                      hasAnalysis ? "bg-green-500/20" : "bg-primary/15",
                    )}
                  >
                    {isCurrentlyAnalyzing ? (
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    ) : (
                      <Check className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {isCurrentlyAnalyzing
                        ? "Analyzing..."
                        : "AI analysis complete"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {isCurrentlyAnalyzing
                        ? "Extracting details, prices & products"
                        : "Title, price, and products extracted"}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={!canSave() || isAnalyzing}
                className={cn(
                  "w-full py-4 rounded-2xl font-semibold text-base transition-all",
                  canSave() && !isAnalyzing
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] shadow-lg shadow-primary/25"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {isAnalyzing ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </span>
                ) : !user ? (
                  "Sign in to continue"
                ) : !hasContent ? (
                  "Paste a link or add photo"
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </DraggableBottomSheet>
        )}
      </AnimatePresence>

      {/* Hidden file inputs OUTSIDE the portal for proper iOS gesture handling */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraInputChange}
        className="hidden"
      />

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />

      {imageAnalysis && (
        <VisualSearchSheet
          open={showVisualSearch}
          onOpenChange={setShowVisualSearch}
          searchTerms={
            imageAnalysis.searchTerms ||
            imageAnalysis.products.map((p) => p.name)
          }
          title={imageAnalysis.title}
          category={imageAnalysis.category}
        />
      )}

      <BarcodeScannerDialog
        open={showBarcodeScanner}
        onOpenChange={setShowBarcodeScanner}
      />
    </>
  );
}
